import { PersistConfig } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/es/storage'; // localStorage

import {
  activeCasesApi,
  adminEventsApi,
  adminUsersApi,
  aircraftStatusesApi,
  alertsApi,
  caseTimelineApi,
  caseTrackingApi,
  domainEventsApi,
  geoLibraryApi,
  hubsResourceReportApi,
  mapTimelineApi,
  newCasesApi,
  nopResourcesApi,
  settingsApi,
  simpleStatisticsApi,
  statsApi,
  surgeonsApi,
  weatherSnapshotApi,
} from '#entities';

import { migrate } from './migrations';

import type { UiSessionState } from '#shared';
import type { RootState } from './rootReducer';

/**
 * Nested persister for the shared UI-session flags. Excludes the transient
 * highlight selection so it resets on reload.
 */
export const uiSessionPersistConfig: PersistConfig<UiSessionState> = {
  key: 'uiSession',
  storage,
  blacklist: [
    'highlightedAircraftRegistration',
    'highlightedAirports',
    'searchText',
  ],
};

/**
 * Root persist configuration with nested persisters.
 * If you need to exclude any slices, add them to blacklist.
 *
 * PERFORMANCE NOTE: only `uiSession` (nested persister), `notification` and
 * `alertFilter` actually persist — everything else is either ephemeral or an
 * RTK Query cache.
 *
 * VERSION HISTORY:
 * - v1: Initial persist implementation
 * - v2: Reset layout state to fix widget ID mismatch on migration
 * - v3: Widget/layout system removed (TMDXNC-45) — strip its persisted keys
 */
export const persistConfig: PersistConfig<RootState> = {
  key: 'root',
  version: 3,
  storage,
  stateReconciler: autoMergeLevel2,
  migrate,
  // whitelist: [], // persist everything by default
  blacklist: [
    'liveSync', // ephemeral WS refetch signals; must reset on reload
    // Session-local playhead intent; a rehydrated Focus would open the map at
    // last week's moment with no way for the operator to tell why.
    'mapFocus',
    // Same argument, same trap: a rehydrated filter opens the map with events
    // missing and no way to tell why, and `absent` != `empty` is one this repo
    // has already paid for. NOTE this ALSO ends the persistence
    // `domains/events`' legend filter used to have via
    // `localStorage['poc:timeline-filter:hidden']` — a deliberate change, and
    // that key was a `poc:`-prefixed unaudited port.
    'eventFilter',
    // Not persisted, for eventFilter's reason: a rehydrated filter is a fleet
    // with aircraft missing and no way to tell why. SpecRef: E036
    'fleetFilter',
    'uiSession', // Has its own nested persister
    activeCasesApi.reducerPath,
    // A live position history served `no-store`. A rehydrated one would open the
    // map with a vehicle standing where it was when the tab was last closed.
    caseTrackingApi.reducerPath,
    caseTimelineApi.reducerPath,
    aircraftStatusesApi.reducerPath,
    hubsResourceReportApi.reducerPath,
    newCasesApi.reducerPath,
    simpleStatisticsApi.reducerPath,
    surgeonsApi.reducerPath,
    statsApi.reducerPath,
    weatherSnapshotApi.reducerPath,
    // A rehydrated cache would hold buckets cut against a window that has since moved.
    mapTimelineApi.reducerPath,
    // Live/transient rolling-log cache (not the cursor — that's a module
    // variable in domainEventCursor.ts, outside Redux and never persisted).
    // Persisting this cache would only bloat storage with stale rows.
    domainEventsApi.reducerPath,
    // Alerts assert what is wrong RIGHT NOW. A rehydrated snapshot would show
    // conditions that may have cleared while the tab was closed, which is the
    // stale-snapshot failure this stream exists to prevent (377:E027).
    alertsApi.reducerPath,
    // Settings are re-read on every socket open; a rehydrated set would show
    // another session's values as current.
    settingsApi.reducerPath,
    // The roster carries user PII (names, emails) and must never persist.
    adminUsersApi.reducerPath,
    // Same reason, one step further: the templates are REAL captured messages, so
    // their caseNames are real clinical identifiers.
    adminEventsApi.reducerPath,
    // Reference data, but a rehydrated cache is handed to the first render — which
    // is a whole class of ordering bug for anything imperative downstream, and
    // cost the map its served-region layers on every tab open but the first
    // (`domains/map/basemap/regions/regionStyle.ts`). One request per tab is the
    // cheaper side of that trade.
    //
    // Added without a version bump, deliberately: the project is pre-production,
    // so the only browsers holding these two keys are development ones. Note what
    // that means, because it is not obvious — a blacklist stops future *writes*
    // only. `autoMergeLevel2` reconciles whatever is in `localStorage` and knows
    // nothing about this list, so a browser that already stored either key keeps
    // rehydrating it until the entry is cleared by hand or by a migration.
    geoLibraryApi.reducerPath,
    nopResourcesApi.reducerPath,
  ], // add slice names here to NOT persist them
};
